/**
 * useCurrentIsoWeekYear
 * Returns the current ISO 8601 week year, respecting time manipulation when enabled.
 * This matches the backend's ISOWeek.GetYear() behavior.
 */
import { useMemo } from 'react';
import { useEffectiveTime } from '../../development/hooks/useEffectiveTime';

interface UseCurrentIsoWeekYearResult {
  /** The current ISO 8601 week year */
  currentIsoYear: number;
  /** Whether time is currently being manipulated */
  isManipulated: boolean;
  /** Whether the time query is loading */
  isLoading: boolean;
}

/**
 * Gets the ISO 8601 week-numbering year for a given date.
 * This can differ from the calendar year near year boundaries.
 *
 * ISO 8601 defines week 1 as the week containing the first Thursday of the year.
 * Days at the start of a year that fall in the previous year's last week belong
 * to that previous year (ISO week year).
 *
 * Examples:
 * - Dec 29, 2025 (Monday) -> ISO year 2026 (part of 2026-W01)
 * - Jan 1, 2026 (Thursday) -> ISO year 2026 (part of 2026-W01)
 * - Dec 28, 2025 (Sunday) -> ISO year 2025 (part of 2025-W52)
 */
function getIsoWeekYear(date: Date): number {
  const d = new Date(date.getTime());
  d.setHours(0, 0, 0, 0);

  // Get the Thursday of this week (ISO 8601 weeks start on Monday)
  // JavaScript: 0 = Sunday, 1 = Monday, ..., 4 = Thursday, 6 = Saturday
  const day = d.getDay();
  const diff = day === 0 ? -3 : 4 - day; // Days until Thursday (Sunday wraps to previous Thursday)
  d.setDate(d.getDate() + diff);

  return d.getFullYear();
}

export function useCurrentIsoWeekYear(): UseCurrentIsoWeekYearResult {
  const { effectiveNow, isManipulated, isLoading } = useEffectiveTime();

  const currentIsoYear = useMemo(
    () => getIsoWeekYear(effectiveNow),
    [effectiveNow]
  );

  return { currentIsoYear, isManipulated, isLoading };
}
